export type MaybeLeaderboardResult = {
  name: string;
  value: number | null;
};
export type LeaderboardResult = {
  name: string;
  value: number;
  latest_book: string | null;
  book_cover: string | null;
  profile_id: number;
  profile_image: string | null;
};

export enum ReactionType {
  Think = '🤔',
  Laugh = '😂',
  Fire = '🔥',
  Heart = '❤',
  Like = '👍',
}

export type Reaction = {
  value: ReactionType;
  reaction_giver: BookwiseUser;
  id: string;
};

export enum FeedbackType {
  Kudos = 'kudos',
  Comment = 'comment'
}


export type Feedback = {
  id: string;
  feedback_giver: BookwiseUser;
  feedback_type: FeedbackType;
  comment?: string;
  image?: string;
  created: number;
  reactions: Reaction[];
  rating?: number;
  is_auto_generated?: boolean;
};

type FeedResultDeviceInfo = {'model': string; 'type': string; 'vendor': string;};

export enum ReadingSessionOrigin {
  Bookwise = 'bookwise',
  Kindle = 'kindle',
}

export type ReadingSession = {
  'ended_at': number;
  'percent_at_end': number;
  'percent_at_start': number;
  'published_at': number;
  'started_at': number;
  'still_ongoing': boolean;
  'summary': string | null;
  'total_time_seconds': number;
  'total_xp': number;
  'id': number;
  'bookwise_profile': BookwiseUser;
  'tracked_book': TrackedBook;
  'is_tts_session': boolean | null;
  'device_info': FeedResultDeviceInfo | { [key: string]: never; };
  'origin': ReadingSessionOrigin;
};

export type ReadingSessionsForTrackedBookAggregates = {
  total_words: number;
  total_time_seconds: number;
  avg_wpm: number;
  progress: number | null;
};

export type DailyProgress = {
  date: string;
  percent_start: number;
  percent_end: number;
};

export type ReadingProgressForTrackedBookResponse = {
  session_count: number;
  aggregates: ReadingSessionsForTrackedBookAggregates;
  daily_progress: DailyProgress[];
};

export type NestedReadingSession = {
  'id': number;
  'owner': BookwiseUser;
  'book_title': string;
  'published_at': number;
  'created': number;
};

export type NestedBookReview = {
  'id': number;
  'owner': BookwiseUser;
  'book_title': string;
  'rating'?: number;
  'user_description'?: string;
  'created': number;
  'image'?: string;
};

export type NestedBookStart = {
  'id': number;
  'owner': BookwiseUser;
  'book_title': string;
  'started_at': number;
  'user_description'?: string;
  'created': number;
  'image'?: string;
};

export type NestedFeedPost = {
  post_type: FeedPostType;
  id: number;
  owner: BookwiseUser;
  created: number;
  reading_session?: NestedReadingSession;
  book_review?: NestedBookReview;
  book_start?: NestedBookStart;
};

export type Follow = {
  id: number;
  follower: BookwiseUser;
  following: BookwiseUser;
  created: string;
};

export enum NotificationType {
  FeedbackOnOwnSession = 'feedback_on_own_session',
  FeedbackOnOthersSession = 'feedback_on_others_session',
  FollowedByUser = 'followed_by_user',
  AtMentionInComment = 'at_mention_in_comment',
  AtMentionInPostDescription = 'at_mention_in_post_description',
  ReactionToOwnComment = 'reaction_on_own_comment',
  SessionPublished = 'session_published',
  PodiumPlacement = 'podium_placement',
  FollowerFinishedBook = 'follower_finished_book',
}

export type NotificationItem = {
  created: string;
  id: number;
  notification_type: NotificationType;
  is_direct: boolean;
  receiver: BookwiseUser;
  post_feedback?: Feedback;
  feed_post?: NestedFeedPost;
  follow?: Follow;
  reaction?: Reaction;
  seen: boolean;
  podium_position?: number;
  body?: string;
  title?: string;
};

export type SessionNotification = {
  post_id?: number;
  feedback_id?: string;
  notification_id?: number;
  podium_position?: number;
};

export type BookwiseUser = {
  profile_id: number;
  is_following: boolean;
  is_following_you: boolean;
  is_self: boolean;
  profile_image: string | null;
  username: string;
  first_name: string;
  last_name: string | null;
};

export type BookwiseUserWihPodium = BookwiseUser & {
  podium_badge?: number;
};

export enum TrackedBookStatus {
  Unsorted = 'unsorted',
  WantToRead = 'want_to_read',
  CurrentlyReading = 'currently_reading',
  Finished = 'finished',
  DidNotFinish = 'did_not_finish',
  Deleted = 'deleted',
}

export enum TrackedBookOrigin {
  BookstoreEbook = 'bookstore_ebook',
  UploadedEbook = 'uploaded_ebook',
  Physical = 'physical',
  Kindle = 'kindle',
  Goodreads = 'goodreads',
  Bookwise = 'bookwise',
}

export type BookstoreProductFeatures = {
  recap_summaries: boolean;
  ghostreader: boolean;
  word_lookup: boolean;
};

export type TrackedBook = {
  id: string;
  created: number;
  updated: number;
  title: string | null;
  author: string | null;
  cover_url: string | null;
  isbn: string | null;
  asin: string | null;
  is_sample: boolean | null;
  progress: number | null;
  creation_origin?: TrackedBookOrigin;
  goodreads_book_id: string | null;
  kindle_user_book_id: number | null;
  status: TrackedBookStatus;
  description: string | null;
  // stripped html text of description
  description_text: string | null;
  short_description: string | null;
  publisher_description_is_low_quality: boolean;
  genre_classification: string[] | null;
  published_at: string | null;

  catalog_product_id: string | null;
  document_id: string | null;
  bookwise_profile_id: number | null;
  last_read_at: number | null;
  started_reading_at: number | null;
  finished_reading_at: number | null;
  status_changed_at: number | null;
  page_count: number | null;
  word_count: number | null;
  reading_time: string | null;

  features: BookstoreProductFeatures;
  session_count: number;
};

export type DailyStats = {
   'number_of_highlights': number;
   'total_time_in_seconds': number;
   'total_xp': number;
};

export type YearlyDailyStats = {
  date: string;
  words: number;
  time_seconds: number;
};

export type YearStreak = {
  days_count: number;
  start: string;
  end: string | null;
};

export type YearlyStats = {
  daily: YearlyDailyStats[];
  streak: YearStreak | null;
};

export type BookstoreProductFormat =
  | 'cd_audio'
  | 'audiobook'
  | 'ebook'
  | 'hardback'
  | 'leather'
  | 'loose_leaf'
  | 'other'
  | 'paperback'
  | 'spiral_bound';

export type SerializedBookstoreProduct = {
  'id': string;
  'ean': number | null;
  'asin': string | null;
  'title': string;
  'author': string;
  'summary': string;
  'cover_url': string | null;
  'language_code': string;
  'publishing_date': string | null;
  'word_count': number | null;
  'format': BookstoreProductFormat;
  'is_drm_protected': boolean;
  'is_fulfillable': boolean;
};

export enum FeedPostType {
  BookStart = 'book_start',
  ReadingSession = 'reading_session',
  BookReview = 'book_review',
}

export type BookStart = {
  id: number;
  bookwise_profile: BookwiseUser;
  tracked_book: TrackedBook;
  started_at: number;
  user_description?: string;
  image?: string;
  created: number;
  updated: number;
  feed_post_id?: number;
};

export type BookReview = {
  id: number;
  bookwise_profile: BookwiseUser;
  tracked_book: TrackedBook;
  user_description?: string;
  image?: string;
  rating?: number;
  created: number;
  updated: number;
  feed_post_id?: number;
};

export type FeedPost = {
  id: number;
  post_type: FeedPostType;
  bookwise_profile: BookwiseUser;
  created: number;
  updated: number;
  reading_session: ReadingSession | null;
  book_start: BookStart | null;
  book_review: BookReview | null;
  feedbacks: Feedback[];
  tracked_book: TrackedBook | null;
};

export type ReadingSessionFeedPost = Omit<FeedPost, 'post_type' | 'reading_session' | 'book_start' | 'book_review'> & {
  post_type: FeedPostType.ReadingSession;
  reading_session: ReadingSession;
  book_start: null;
  book_review: null;
};

export type BookStartFeedPost = Omit<FeedPost, 'post_type' | 'reading_session' | 'book_start' | 'book_review'> & {
  post_type: FeedPostType.BookStart;
  reading_session: null;
  book_start: BookStart;
  book_review: null;
};

export type BookReviewFeedPost = Omit<FeedPost, 'post_type' | 'reading_session' | 'book_start' | 'book_review'> & {
  post_type: FeedPostType.BookReview;
  reading_session: null;
  book_start: null;
  book_review: BookReview;
};

export type FeedPostIdGroup = {
  primaryFeedPostId: FeedPost['id'];
  extraPostIds: FeedPost['id'][];
};

export type FeedPostGroup = {
  primaryFeedPost: FeedPost;
  extraPosts: FeedPost[];
};

export type ReadingSessionFeedPostGroup = {
  primaryFeedPost: ReadingSessionFeedPost;
  extraPosts: ReadingSessionFeedPost[];
};

export type BookStartFeedPostGroup = {
  primaryFeedPost: BookStartFeedPost;
  extraPosts: [];
};

export type BookReviewFeedPostGroup = {
  primaryFeedPost: BookReviewFeedPost;
  extraPosts: [];
};

export type SupplementalHighlight = {
  id: number;
  text: string;
  location: number;
  location_type: string;
};
